import { Mail, Phone, BadgeCheck, ShieldCheck, CircleAlert } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { ProfileShell } from "../../components/profile/ProfileShell";

// Defined at module scope (NOT inside VerificationPage) — otherwise React would
// see a fresh component type on every parent re-render and destroy+recreate the
// entire button + its inner DOM each time, costing visible perf and breaking
// focus / animations. This is the textbook fix for `no-unstable-nested-components`.
function VerificationRow({ icon: Icon, label, verified, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 p-4 rounded-xl bg-[#101824] border border-white/5 hover:bg-white/[0.03] transition-colors text-left"
    >
      <div
        className="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
        style={{
          background: verified ? "rgba(25,165,92,0.15)" : "rgba(255,255,255,0.04)",
          color: verified ? "#21c46e" : "rgba(255,255,255,0.6)",
        }}
      >
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white font-medium text-[15px]">{label}</div>
        <div className="text-xs mt-0.5" style={{ color: verified ? "#21c46e" : "#f59e0b" }}>
          {verified ? "Verified" : "Not verified"}
        </div>
      </div>
      {verified ? (
        <BadgeCheck size={20} className="text-[#21c46e]" />
      ) : (
        <CircleAlert size={20} className="text-amber-400/80" />
      )}
    </button>
  );
}

export default function VerificationPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <ProfileShell title="Verification">
      <div className="py-2 pb-10 space-y-3" data-testid="verification-page">
        <div
          className="rounded-xl p-4 relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, rgba(25,165,92,0.18) 0%, rgba(25,165,92,0.04) 60%)",
            border: "1px solid rgba(25,165,92,0.2)",
          }}
        >
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-[#21c46e]" />
            <span className="text-white font-semibold text-sm">Account security</span>
          </div>
          <p className="text-white/65 text-sm mt-2 leading-relaxed">
            Verify your email, phone and identity to unlock higher withdrawal
            limits and access exclusive VIP tournaments.
          </p>
        </div>

        <VerificationRow
          icon={Mail}
          label="Email verification"
          verified={user?.email_verified}
          onClick={() => navigate("/profile/email-verify")}
        />
        <VerificationRow
          icon={Phone}
          label="Phone verification"
          verified={user?.phone_verified}
          onClick={() => navigate("/profile/phone-verify")}
        />
        <VerificationRow
          icon={BadgeCheck}
          label="Identity (KYC)"
          verified={user?.kyc_status === "verified"}
          onClick={() => navigate("/profile/kyc")}
        />
      </div>
    </ProfileShell>
  );
}
